


import {
  defineComponent,
  PropType,
  onMounted,
  computed,
  reactive,
} from '@vue/composition-api';
import type {
  HokkaidoAirportReportCar,
  HokkaidoAirportReport,
} from '@/models/externalReports/hokkaidoAirport/spHokkaidoAirportReport';
import type {
  HokkaidoAirportSettings,
  HokkaidoAirportLabelSp,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';
import type {
  HokkaidoAirportReportEndParams,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportReportRequest';
import hokkaidoAirportReportApi from '@/apis/externalReports/hokkaidoAirport/hokkaido_airport_report';
import useSpeech from '@/composables/useSpeech';
import { useRequestGuard } from '@/composables/useRequestGuard';
import { useRoute } from '@/hooks/useRoute';
import { roundToIntervalMinutes } from '@/lib/dateTimeHelper';
import { REPORT_TS_ROUND_MINUTES } from '@/consts/externalReports/hokkaidoAirport';

interface WorkingState {
  showConfirmStartWorkingModal: boolean;
  showConfirmTemporarilyStopWorkingModal: boolean;
  showConfirmStopWorkingModal: boolean;
  hokkaidoAirportReport: HokkaidoAirportReport;
}
export default defineComponent({
  props: {
    hokkaidoAirportCar: {
      type: Object as PropType<HokkaidoAirportReportCar>,
      required: true,
    },
    userSettings: {
      type: Object as PropType<HokkaidoAirportSettings>,
      required: true,
    },
  },
  emits: [
    'suspendWork',
    'editContentAndRoad',
    'showErrorOnCommon',
    'addPhotoMemo',
  ],
  setup(props, { emit }) {
    const state = reactive<WorkingState>({
      showConfirmStartWorkingModal: false,
      showConfirmTemporarilyStopWorkingModal: false,
      showConfirmStopWorkingModal: false,
      hokkaidoAirportReport: props.hokkaidoAirportCar.report,
    });
    const { doSpeech } = useSpeech();
    const { router } = useRoute();
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      emit('showErrorOnCommon', errorMessage);
    });

    const spLabels = computed<HokkaidoAirportLabelSp>(() => {
      return props.userSettings.sp;
    });

    const stopWorking = async() => {
      state.showConfirmStopWorkingModal = false;
      const operationEndTime = roundToIntervalMinutes(new Date(), REPORT_TS_ROUND_MINUTES);
      const params: HokkaidoAirportReportEndParams = {
        operation_end_ts: operationEndTime,
      };
      await doRequest(async() => {
        state.hokkaidoAirportReport = await hokkaidoAirportReportApi.updateHokkaidoAirportReportEnd(
          state.hokkaidoAirportReport.id,
          params,
        );
      }, '作業終了に失敗しました。再度操作を行ってください。');
      doSpeech(spLabels.value.message.confirm_end_work);
      emit('suspendWork', props.userSettings.choices.status.running.key, state.hokkaidoAirportReport);
    };

    const addPhotoMemo = () => {
      emit('addPhotoMemo');
    };

    const editContentAndRoad = () => {
      emit('editContentAndRoad');
    };

    onMounted(() => {
      if (state.hokkaidoAirportReport.id === 0) {
        router.replace({name: 'Login'});
      }
    });

    return {
      state,
      isRequesting,
      spLabels,
      addPhotoMemo,
      editContentAndRoad,
      stopWorking,
    };
  },
});
